import { useCallback, useState } from 'react';
import { TAG_PRICE } from '@/data/titleColorPalette';
import { useLocale } from '@/context/LocaleContext';
import { useSilkBalance } from '@/hooks/useSilkBalance';
import { useTags, useTitleManagerMutations, type TagRow } from '@/hooks/useTitleManager';
import { BuyTagModal } from './BuyTagModal';
import { isRowActive, notifyFromResult, type TmNotifyType } from './titleManagerUtils';

interface TagTabProps {
  onNotify: (msg: string, type?: TmNotifyType) => void;
}

export function TagTab({ onNotify }: TagTabProps) {
  const { t } = useLocale();
  const { data: tags = [], isLoading } = useTags();
  const { buyTag, applyTag, removeTag, reloadTags } = useTitleManagerMutations();

  const [tagInput, setTagInput] = useState('');
  const [inputError, setInputError] = useState(false);
  const [buyOpen, setBuyOpen] = useState(false);

  const { data: balance, isLoading: balanceLoading, refetch: refetchBalance } = useSilkBalance(buyOpen);

  const openBuyModal = useCallback(async () => {
    if (!tagInput.trim()) {
      setInputError(true);
      window.setTimeout(() => setInputError(false), 2000);
      return;
    }
    await refetchBalance();
    setBuyOpen(true);
  }, [refetchBalance, tagInput]);

  const handleBuyConfirm = useCallback(async () => {

    try {
      const result = await buyTag.mutateAsync(tagInput.trim());
      if (result.success) {
        setBuyOpen(false);
        setTagInput('');
        await reloadTags();
      }
      notifyFromResult(result, t, onNotify, 'tm_error_buying_tag');
    } catch {
      onNotify(t('tm_error_buying_tag'), 'danger');
    }
  }, [buyTag, onNotify, reloadTags, t, tagInput]);

  const handleApply = useCallback(
    async (row: TagRow) => {
      try {
        const result = await applyTag.mutateAsync(row.id);
        if (result.success) await reloadTags();
        notifyFromResult(result, t, onNotify, 'tm_error_applying_tag');
      } catch {
        onNotify(t('tm_error_applying_tag'), 'danger');
      }
    },
    [applyTag, onNotify, reloadTags, t],
  );

  const handleRemove = useCallback(
    async (row: TagRow) => {
      try {
        const result = await removeTag.mutateAsync(row.id);
        if (result.success) await reloadTags();
        notifyFromResult(result, t, onNotify, 'tm_error_removing_tag');
      } catch {
        onNotify(t('tm_error_removing_tag'), 'danger');
      }
    },
    [onNotify, reloadTags, removeTag, t],
  );

  return (
    <>
      <div className="tagmanager-content">
        <div className="tagmanager-grid">
          <div className="tagmanager-list">
            <table className="tagmanager-table">
              <thead>
                <tr>
                  <th>{t('tm_tag_column')}</th>
                  <th>{t('tm_actions_column')}</th>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <tr>
                    <td colSpan={2} style={{ textAlign: 'center', padding: 20, color: '#888' }}>
                      {t('loading_data')}
                    </td>
                  </tr>
                ) : tags.length === 0 ? (
                  <tr>
                    <td colSpan={2} style={{ textAlign: 'center', padding: 20, color: '#888' }}>
                      {t('tm_no_tags')}
                    </td>
                  </tr>
                ) : (
                  tags.map((row) => {
                    const active = isRowActive(row.isactive);
                    return (
                      <tr key={row.id} className={active ? 'tm-row-active' : undefined}>
                        <td>
                          <span className="tag-text">{row.tag}</span>
                        </td>
                        <td>
                          <div className="titlemanager-actions">
                            <button
                              type="button"
                              className="titlemanager-action-btn"
                              title={t('tm_apply_tag')}
                              disabled={active || applyTag.isPending}
                              onClick={() => void handleApply(row)}
                            >
                              <i className="fas fa-check" aria-hidden />
                            </button>
                            <button
                              type="button"
                              className="titlemanager-action-btn"
                              title={t('tm_remove_tag')}
                              disabled={removeTag.isPending}
                              onClick={() => void handleRemove(row)}
                            >
                              <i className="fas fa-trash" aria-hidden />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          <div className="tagmanager-editor">
            <div className="tagmanager-input-group">
              <input
                type="text"
                className={`tagmanager-input${inputError ? ' tm-input-error' : ''}`}
                placeholder={t('tm_enter_tag')}
                maxLength={64}
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
              />
              <button
                type="button"
                className="tagmanager-btn"
                disabled={buyTag.isPending}
                onClick={() => void openBuyModal()}
              >
                {buyTag.isPending ? t('tm_processing') : t('tm_buy')}
              </button>
            </div>
            <div className="tagmanager-prices">
              <div className="price-item">
                <span>{t('tm_price_tag')}:</span>
                <span className="price-amount">{TAG_PRICE} Silks</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <BuyTagModal
        open={buyOpen}
        tag={tagInput.trim()}
        price={TAG_PRICE}
        balance={balance ?? null}
        balanceLoading={balanceLoading}
        confirming={buyTag.isPending}
        onConfirm={() => void handleBuyConfirm()}
        onClose={() => {
          if (!buyTag.isPending) setBuyOpen(false);
        }}
      />
    </>
  );
}
